'use client';

import React, { useState, useEffect } from 'react';
import Script from 'next/script';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

interface Subscription {
  plan: string;
  status: string;
  amount_inr: number;
  royalty_percentage: number;
  max_releases_per_month: number;
  current_period_start: string;
  current_period_end: string | null;
}

declare global {
  interface Window { Razorpay: any; }
}

// ─── Feature Matrix Data ──────────────────────────────────────────────────────

const FEATURE_ROWS = [
  { category: 'Distribution', label: 'Streaming Stores', free: '150+ standard', pro: '450+ global', label_tier: '450+ + VIP Pipeline' },
  { category: 'Distribution', label: 'Royalty Share', free: '85% to artist', pro: '100% keep all', label_tier: '100% keep all' },
  { category: 'Distribution', label: 'Monthly Releases', free: '5 releases/mo', pro: 'Unlimited', label_tier: 'Unlimited' },
  { category: 'Distribution', label: 'Artist Profiles', free: '1 profile only', pro: '1 dedicated profile', label_tier: 'Up to 5 profiles' },
  { category: 'Distribution', label: 'Custom Release Date', free: false, pro: true, label_tier: true },
  { category: 'Distribution', label: 'Pre-Save SmartLinks', free: false, pro: true, label_tier: true },
  { category: 'Identifiers', label: 'Free Auto ISRC & UPC', free: true, pro: true, label_tier: true },
  { category: 'Monetization', label: 'YouTube Content ID', free: false, pro: true, label_tier: true },
  { category: 'Monetization', label: 'YouTube OAC (♪ Badge)', free: false, pro: true, label_tier: true },
  { category: 'Monetization', label: 'Spotify for Artists Access', free: false, pro: true, label_tier: true },
  { category: 'Monetization', label: 'Apple Music for Artists', free: false, pro: true, label_tier: true },
  { category: 'Monetization', label: 'Royalty Splits', free: false, pro: '2 collaborators', label_tier: 'Unlimited + auto-payout' },
  { category: 'Label Features', label: 'Custom Label Branding (P)/(C)', free: false, pro: false, label_tier: true },
  { category: 'Label Features', label: 'YouTube Channel Whitelisting', free: false, pro: false, label_tier: true },
  { category: 'Label Features', label: 'Multi-Artist Analytics Dashboard', free: false, pro: false, label_tier: true },
  { category: 'Label Features', label: 'CSV / Excel Financial Export', free: false, pro: false, label_tier: true },
  { category: 'QC & Delivery', label: 'QC Turnaround', free: '3–5 days standard', pro: '24–48 hr fast-track', label_tier: '12–24 hr VIP' },
  { category: 'QC & Delivery', label: 'Monthly Royalty Reporting', free: true, pro: true, label_tier: true },
  { category: 'Support', label: 'Support Channel', free: 'Standard email', pro: '24/7 priority ticket', label_tier: 'Dedicated VIP + WhatsApp' },
  { category: 'Support', label: 'Account Manager', free: false, pro: false, label_tier: '1-on-1 VIP manager' },
];

const CATEGORIES = [...new Set(FEATURE_ROWS.map((r) => r.category))];

function FeatureCell({ value }: { value: boolean | string }) {
  if (value === true) return <Icon name="CheckCircleIcon" size={18} style={{ color: '#10b981' }} />;
  if (value === false) return <Icon name="XCircleIcon" size={18} style={{ color: '#ef4444', opacity: 0.5 }} />;
  return <span className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>{value}</span>;
}

export default function PlansPage() {
  const [currentPlan, setCurrentPlan] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState<string | null>(null);
  const [razorpayReady, setRazorpayReady] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [showMatrix, setShowMatrix] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'annual' | 'monthly'>('annual');

  // INR-only pricing
  const ANNUAL_PRICES = { pro: '₹1,499/yr', label: '₹3,499/yr', proRaw: 1499, labelRaw: 3499 };
  const MONTHLY_PRICES = { pro: '₹149/mo', label: '₹349/mo', proRaw: 149, labelRaw: 349 };
  const prices = billingCycle === 'annual' ? ANNUAL_PRICES : MONTHLY_PRICES;

  const PLANS = [
    {
      id: 'free',
      name: 'Free Starter',
      tagline: 'For emerging artists — single artist only',
      price: '₹0',
      period: 'forever',
      royalty: '85% royalty',
      releases: '5 releases/mo',
      stores: '150+ stores',
      color: '#94a3b8',
      bg: 'var(--card)',
      border: 'var(--border)',
      badge: null,
      highlights: [
        '1 Artist Profile (single artist only)',
        'Free auto ISRC & UPC',
        '150+ streaming stores',
        '3–5 day QC turnaround',
        'Monthly royalty reporting',
        'Standard email support',
      ],
      restrictions: ['No YouTube Content ID', 'No OAC request', 'No custom release date', 'Second artist blocked'],
      cta: 'Activate Free Plan',
      ctaBg: 'var(--card-elevated)',
      ctaColor: 'var(--foreground)',
      ctaBorder: 'var(--border)',
    },
    {
      id: 'artist_pro',
      name: 'Artist Pro',
      tagline: 'For independent solo artists — single artist only',
      price: prices.pro,
      period: billingCycle === 'annual' ? 'per year' : 'per month',
      royalty: '100% royalty',
      releases: 'Unlimited',
      stores: '450+ stores',
      color: 'var(--primary-light)',
      bg: 'rgba(124,58,237,0.05)',
      border: 'rgba(124,58,237,0.4)',
      badge: 'Most Popular',
      highlights: [
        '1 Artist Profile (single artist only)',
        'YouTube Content ID monetization',
        'YouTube OAC (♪ badge) portal',
        'Spotify & Apple Music for Artists',
        'Custom release date scheduling',
        'Pre-Save SmartLinks',
        '24–48 hr fast-track QC',
        'Royalty splits (2 collaborators)',
        '24/7 priority support',
      ],
      restrictions: ['Second artist blocked — upgrade to Label Elite'],
      cta: `Get Artist Pro`,
      ctaBg: 'var(--primary)',
      ctaColor: 'white',
      ctaBorder: 'transparent',
      amountInr: billingCycle === 'annual' ? 1499 : 149,
    },
    {
      id: 'label_producer',
      name: 'Label & Producer Elite',
      tagline: 'For record labels, studios & multi-artist rosters',
      price: prices.label,
      period: billingCycle === 'annual' ? 'per year' : 'per month',
      royalty: '100% royalty',
      releases: 'Unlimited',
      stores: '450+ + VIP',
      color: '#f59e0b',
      bg: 'rgba(245,158,11,0.04)',
      border: 'rgba(245,158,11,0.35)',
      badge: 'Best for Labels',
      highlights: [
        'Up to 5 artist profiles',
        'Custom label branding (P)/(C)',
        'YouTube Channel Whitelisting',
        'OAC management for all roster artists',
        '12–24 hr VIP QC review',
        'Unlimited royalty splits + auto-payout',
        'Multi-artist analytics + CSV export',
        'Dedicated 1-on-1 VIP account manager',
        'WhatsApp support',
      ],
      restrictions: [],
      cta: `Get Label Elite`,
      ctaBg: '#f59e0b',
      ctaColor: 'white',
      ctaBorder: 'transparent',
      amountInr: billingCycle === 'annual' ? 3499 : 349,
    },
  ];

  useEffect(() => {
    async function fetchSubscription() {
      try {
        const res = await fetch('/api/subscriptions');
        if (!res.ok) return;
        const json = await res.json();
        setCurrentPlan(json.subscription);
      } catch (e) {
        console.error('Failed to load subscription:', e);
      } finally {
        setLoading(false);
      }
    }
    fetchSubscription();
  }, []);

  function showMessage(type: 'success' | 'error' | 'info', text: string) {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 6000);
  }

  async function handleFreeActivation() {
    setActivating('free');
    try {
      const res = await fetch('/api/subscriptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: 'free' }),
      });
      const json = await res.json();
      if (!res.ok) { showMessage('error', json.error || 'Failed to activate plan'); return; }
      setCurrentPlan(json.subscription);
      showMessage('success', 'Free Starter plan activated!');
    } catch (e: any) {
      showMessage('error', e.message);
    } finally {
      setActivating(null);
    }
  }

  async function handlePaidPlan(planId: string, planName: string, amountInr: number) {
    // Check window.Razorpay directly — more reliable than onLoad state
    if (typeof window === 'undefined' || !window.Razorpay) {
      // Try loading the script dynamically if not yet available
      try {
        await new Promise<void>((resolve, reject) => {
          if (window.Razorpay) { resolve(); return; }
          const existing = document.getElementById('razorpay-checkout-js');
          if (existing) {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', reject);
            return;
          }
          const script = document.createElement('script');
          script.id = 'razorpay-checkout-js';
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = () => resolve();
          script.onerror = reject;
          document.body.appendChild(script);
        });
      } catch {
        showMessage('error', 'Payment gateway failed to load. Please refresh and try again.');
        return;
      }
    }
    setActivating(planId);
    try {
      const orderRes = await fetch('/api/razorpay/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planId }),
      });
      const orderData = await orderRes.json();
      if (!orderRes.ok) { showMessage('error', orderData.error || 'Failed to initiate payment'); setActivating(null); return; }

      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: 'INR',
        name: 'TubeNexa',
        description: `${planName} — ${billingCycle === 'annual' ? 'Annual' : 'Monthly'} Subscription`,
        image: '/assets/images/Brown_Minimalist_YouTube_Banner__3000_x_3000_px_-1787252454715.png',
        order_id: orderData.orderId,
        handler: async (response: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => {
          try {
            const verifyRes = await fetch('/api/razorpay/verify-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ...response, plan: planId }),
            });
            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              setCurrentPlan(verifyData.subscription);
              showMessage('success', `🎉 ${planName} activated! Welcome to TubeNexa Pro.`);
            } else {
              showMessage('error', verifyData.error || 'Payment verification failed. Contact support.');
            }
          } catch {
            showMessage('error', 'Payment received but verification failed. Contact support with payment ID: ' + response.razorpay_payment_id);
          } finally {
            setActivating(null);
          }
        },
        modal: { ondismiss: () => { setActivating(null); showMessage('info', 'Payment cancelled.'); } },
        prefill: {},
        theme: { color: '#7c3aed' },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (res: any) => { showMessage('error', `Payment failed: ${res.error.description}`); setActivating(null); });
      rzp.open();
    } catch (e: any) {
      showMessage('error', e.message || 'Something went wrong.');
      setActivating(null);
    }
  }

  async function handlePlanSelect(plan: typeof PLANS[0]) {
    if (plan.id === 'free') await handleFreeActivation();
    else await handlePaidPlan(plan.id, plan.name, plan.amountInr!);
  }

  const msgColors = { success: '#10b981', error: '#ef4444', info: '#818cf8' };
  const msgBgs = { success: 'rgba(16,185,129,0.1)', error: 'rgba(239,68,68,0.1)', info: 'rgba(99,102,241,0.1)' };
  const msgBorders = { success: 'rgba(16,185,129,0.3)', error: 'rgba(239,68,68,0.3)', info: 'rgba(99,102,241,0.3)' };

  return (
    <AppLayout>
      <Script src="https://checkout.razorpay.com/v1/checkout.js" strategy="afterInteractive" onLoad={() => setRazorpayReady(true)} />

      <div className="space-y-6 max-w-6xl mx-auto">

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-2" style={{ background: 'rgba(124,58,237,0.1)', color: 'var(--primary-light)', border: '1px solid rgba(124,58,237,0.2)' }}>
            <Icon name="StarIcon" size={12} />
            World-Class Music Distribution
          </div>
          <h1 className="text-3xl font-black" style={{ color: 'var(--foreground)' }}>Choose Your Plan</h1>
          <p className="text-sm max-w-lg mx-auto" style={{ color: 'var(--muted-foreground)' }}>
            From emerging artists to major labels — distribute globally, keep your royalties, grow your career.
          </p>
        </div>

        {/* Billing Toggle Row */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          {/* INR Badge */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.12)' }}>
              <Icon name="CurrencyRupeeIcon" size={18} style={{ color: '#818cf8' }} />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>
                Indian Rupee (₹ INR) · Razorpay / UPI
              </p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                All prices in INR — India
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Billing cycle toggle */}
            <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)' }}>
              <button
                onClick={() => setBillingCycle('annual')}
                className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                style={{ background: billingCycle === 'annual' ? 'var(--primary)' : 'transparent', color: billingCycle === 'annual' ? 'white' : 'var(--muted-foreground)' }}
              >
                Annual <span className="ml-1 text-2xs" style={{ color: billingCycle === 'annual' ? 'rgba(255,255,255,0.7)' : '#10b981' }}>Save 20%</span>
              </button>
              <button
                onClick={() => setBillingCycle('monthly')}
                className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                style={{ background: billingCycle === 'monthly' ? 'var(--primary)' : 'transparent', color: billingCycle === 'monthly' ? 'white' : 'var(--muted-foreground)' }}
              >
                Monthly
              </button>
            </div>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className="flex items-center gap-3 p-4 rounded-xl max-w-2xl mx-auto" style={{ background: msgBgs[message.type], border: `1px solid ${msgBorders[message.type]}` }}>
            <Icon name={message.type === 'success' ? 'CheckCircleIcon' : message.type === 'info' ? 'InformationCircleIcon' : 'ExclamationCircleIcon'} size={18} style={{ color: msgColors[message.type] }} />
            <p className="text-sm font-semibold" style={{ color: msgColors[message.type] }}>{message.text}</p>
          </div>
        )}

        {/* Current Plan Badge */}
        {!loading && currentPlan && (
          <div className="flex items-center justify-center gap-3 p-3 rounded-xl max-w-sm mx-auto" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)' }}>
            <Icon name="CheckBadgeIcon" size={18} style={{ color: 'var(--primary-light)' }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--primary-light)' }}>
              Active: {PLANS.find((p) => p.id === currentPlan.plan)?.name || currentPlan.plan}
            </p>
            {currentPlan.current_period_end && (
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                · Renews {new Date(currentPlan.current_period_end).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
              </span>
            )}
          </div>
        )}

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS.map((plan) => {
            const isActive = currentPlan?.plan === plan.id;
            const isProcessing = activating === plan.id;
            return (
              <div
                key={plan.id}
                className="rounded-2xl p-5 flex flex-col relative"
                style={{ background: plan.bg, border: `1px solid ${plan.border}`, boxShadow: plan.badge ? '0 0 30px rgba(124,58,237,0.08)' : 'none' }}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white whitespace-nowrap" style={{ background: plan.id === 'label_producer' ? '#f59e0b' : 'var(--primary)' }}>
                    {plan.badge}
                  </div>
                )}

                {/* Plan Header */}
                <div className="mb-4">
                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--muted-foreground)' }}>{plan.tagline}</p>
                  <h3 className="text-lg font-black mb-2" style={{ color: 'var(--foreground)' }}>{plan.name}</h3>
                  <div className="flex items-baseline gap-1.5 mb-2">
                    <span className="text-3xl font-black" style={{ color: plan.color }}>{plan.price}</span>
                    <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{plan.period}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981' }}>{plan.royalty}</span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(99,102,241,0.12)', color: '#818cf8' }}>{plan.releases}</span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(6,182,212,0.12)', color: '#06b6d4' }}>{plan.stores}</span>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-1.5 flex-1 mb-4">
                  {plan.highlights.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs" style={{ color: 'var(--secondary-foreground)' }}>
                      <Icon name="CheckIcon" size={13} style={{ color: '#10b981', flexShrink: 0, marginTop: 1 }} />
                      {f}
                    </li>
                  ))}
                  {plan.restrictions.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs" style={{ color: 'var(--muted-foreground)', opacity: 0.7 }}>
                      <Icon name="XMarkIcon" size={13} style={{ color: '#ef4444', flexShrink: 0, marginTop: 1 }} />
                      {f}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  onClick={() => handlePlanSelect(plan)}
                  disabled={isActive || isProcessing}
                  className="w-full py-3 rounded-xl text-sm font-bold transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                  style={{ background: isActive ? 'rgba(16,185,129,0.15)' : plan.ctaBg, color: isActive ? '#10b981' : plan.ctaColor, border: `1px solid ${isActive ? 'rgba(16,185,129,0.4)' : plan.ctaBorder}` }}
                >
                  {isProcessing ? (
                    <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Processing...</>
                  ) : isActive ? (
                    <><Icon name="CheckBadgeIcon" size={15} />Current Plan</>
                  ) : (
                    plan.cta
                  )}
                </button>

                {plan.id !== 'free' && !isActive && (
                  <p className="text-center text-2xs mt-2" style={{ color: 'var(--muted-foreground)' }}>
                    🔒 Razorpay · UPI · Cards · NetBanking
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Feature Gate Notice */}
        <div className="rounded-2xl p-4" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <div className="flex items-start gap-3">
            <Icon name="ShieldExclamationIcon" size={18} style={{ color: '#ef4444', flexShrink: 0, marginTop: 1 }} />
            <div>
              <p className="text-sm font-bold mb-1" style={{ color: '#ef4444' }}>Feature Gate Enforcement</p>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                <strong style={{ color: 'var(--foreground)' }}>Free Starter:</strong> YouTube Content ID, OAC requests, and custom label metadata are blocked at the API level. Limited to 1 artist profile only.{' '}
                <strong style={{ color: 'var(--foreground)' }}>Artist Pro:</strong> Single artist account — adding a second artist profile is blocked. Multi-artist profile creation is restricted to Label Elite only.{' '}
                Upgrade to Label Elite to manage multiple artists.
              </p>
            </div>
          </div>
        </div>

        {/* Full Feature Comparison Matrix Toggle */}
        <div className="text-center">
          <button
            onClick={() => setShowMatrix(!showMatrix)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold"
            style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
          >
            <Icon name={showMatrix ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={15} />
            {showMatrix ? 'Hide' : 'Show'} Full Feature Comparison
          </button>
        </div>

        {/* Feature Matrix Table */}
        {showMatrix && (
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
            {/* Table Header */}
            <div className="grid grid-cols-4 gap-0" style={{ background: 'var(--card-elevated)', borderBottom: '1px solid var(--border)' }}>
              <div className="px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>Feature</p>
              </div>
              {[
                { name: 'Free Starter', color: '#94a3b8' },
                { name: 'Artist Pro', color: 'var(--primary-light)' },
                { name: 'Label Elite', color: '#f59e0b' },
              ].map((h) => (
                <div key={h.name} className="px-4 py-3 text-center">
                  <p className="text-xs font-bold" style={{ color: h.color }}>{h.name}</p>
                </div>
              ))}
            </div>

            {/* Rows grouped by category */}
            {CATEGORIES.map((cat) => (
              <React.Fragment key={cat}>
                <div className="px-4 py-2" style={{ background: 'rgba(99,102,241,0.06)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
                  <p className="text-2xs font-bold uppercase tracking-widest" style={{ color: '#818cf8' }}>{cat}</p>
                </div>
                {FEATURE_ROWS.filter((r) => r.category === cat).map((row, i) => (
                  <div
                    key={row.label}
                    className="grid grid-cols-4 gap-0"
                    style={{ background: i % 2 === 0 ? 'var(--card)' : 'var(--card-elevated)', borderBottom: '1px solid var(--border)' }}
                  >
                    <div className="px-4 py-3">
                      <p className="text-xs font-medium" style={{ color: 'var(--foreground)' }}>{row.label}</p>
                    </div>
                    <div className="px-4 py-3 flex items-center justify-center">
                      <FeatureCell value={row.free} />
                    </div>
                    <div className="px-4 py-3 flex items-center justify-center">
                      <FeatureCell value={row.pro} />
                    </div>
                    <div className="px-4 py-3 flex items-center justify-center">
                      <FeatureCell value={row.label_tier} />
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}

            {/* Upgrade CTAs in matrix footer */}
            <div className="grid grid-cols-4 gap-0 p-4" style={{ background: 'var(--card-elevated)', borderTop: '1px solid var(--border)' }}>
              <div />
              {PLANS.map((plan) => {
                const isActive = currentPlan?.plan === plan.id;
                return (
                  <div key={plan.id} className="px-2 flex justify-center">
                    <button
                      onClick={() => handlePlanSelect(plan)}
                      disabled={isActive || activating === plan.id}
                      className="w-full py-2 rounded-xl text-xs font-bold disabled:opacity-60"
                      style={{ background: isActive ? 'rgba(16,185,129,0.15)' : plan.ctaBg, color: isActive ? '#10b981' : plan.ctaColor, border: `1px solid ${isActive ? 'rgba(16,185,129,0.4)' : plan.ctaBorder || 'var(--border)'}` }}
                    >
                      {isActive ? '✓ Active' : plan.id === 'free' ? 'Activate' : 'Upgrade'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* INR Pricing Reference */}
        <div className="rounded-2xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>TubeNexa Distribution — INR Pricing</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { plan: 'Free Starter', price: '₹0', period: 'forever', color: '#94a3b8', artists: '1 artist only' },
              { plan: 'Artist Pro', price: '₹1,499/yr or ₹149/mo', period: 'annual / monthly', color: 'var(--primary-light)', artists: '1 artist only' },
              { plan: 'Label & Producer Elite', price: '₹3,499/yr or ₹349/mo', period: 'annual / monthly', color: '#f59e0b', artists: 'Up to 5 artists' },
            ].map((r) => (
              <div key={r.plan} className="p-3 rounded-xl" style={{ background: 'var(--card-elevated)', border: '1px solid var(--border)' }}>
                <p className="text-sm font-bold mb-1" style={{ color: r.color }}>🇮🇳 {r.plan}</p>
                <p className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>{r.price}</p>
                <p className="text-2xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{r.artists} · Razorpay / UPI</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </AppLayout>
  );
}
